import type { DisposePool } from "@phosphor/utils";
import type { WorldStateImpl } from "#scripts/vmc/WorldState-impl.ts";
import type { World } from "#scripts/vmc/WorldState.ts";
import { CFeatureFlags } from "#scripts/world-state-demo/scrolly-rectangle-demo/components.ts";
import { rootLogger } from "./rootLogger.ts";

/**
 * Set up IntersectionObservers for any element with data-target-uid and data-set-flags
 * Generic system that finds elements and updates WorldState entities based on scroll position
 */
export function setupScrollObservers(world: WorldStateImpl, containerElement: HTMLElement, pool: DisposePool) {
  const elementsWithFlags = document.querySelectorAll("[data-target-uid][data-set-flags]");

  if (elementsWithFlags.length === 0) {
    rootLogger._debug("No elements with data-target-uid and data-set-flags found, skipping scroll observers");
    return;
  }

  // Track active throttle timers per entity
  const activeTransitions = new Map<World.UID, ReturnType<typeof setTimeout>[]>();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const element = entry.target as HTMLElement;
        const targetUID = element.dataset.targetUid;
        const setFlags = element.dataset.setFlags;

        if (!targetUID || !setFlags) {
          continue;
        }

        const featureFlagsAtom = world.getComponentAtom(targetUID as World.UID, CFeatureFlags);
        const featureFlags = world.store.get(featureFlagsAtom);

        if (!featureFlags) {
          rootLogger._warn(`Entity ${targetUID} missing CFeatureFlags component`);
          continue;
        }

        // Cancel any in-progress transitions for this entity
        const existingTimers = activeTransitions.get(targetUID as World.UID);
        if (existingTimers) {
          for (const timer of existingTimers) {
            clearTimeout(timer);
          }
        }

        const flagsArray = setFlags
          .split(",")
          .map((f) => f.trim())
          .filter(Boolean);
        const targetFlags = new Set(flagsArray);
        const currentFlags = world.store.get(featureFlags.flagsAtom);

        // Calculate which flags to add and which to remove
        const toAdd = Array.from(targetFlags).filter((f) => !currentFlags.has(f));
        const toRemove = Array.from(currentFlags).filter((f) => !targetFlags.has(f));

        // Interleave additions and removals for smooth transition
        const changes: Array<{ type: "add" | "remove"; flag: string }> = [];
        const maxLength = Math.max(toAdd.length, toRemove.length);
        for (let i = 0; i < maxLength; i++) {
          if (i < toAdd.length) changes.push({ type: "add", flag: toAdd[i] });
          if (i < toRemove.length) changes.push({ type: "remove", flag: toRemove[i] });
        }

        // Apply changes one at a time with 150ms delay
        const timers: ReturnType<typeof setTimeout>[] = [];
        changes.forEach((change, index) => {
          const timer = setTimeout(() => {
            const current = new Set(world.store.get(featureFlags.flagsAtom));
            if (change.type === "add") {
              current.add(change.flag);
            } else {
              current.delete(change.flag);
            }
            world.store.set(featureFlags.flagsAtom, current);
            rootLogger._debug(`${change.type === "add" ? "Added" : "Removed"} flag ${change.flag} for ${targetUID}`);
          }, index * 150);
          timers.push(timer);
        });

        activeTransitions.set(targetUID as World.UID, timers);

        for (const el of document.querySelectorAll(`[data-target-uid="${targetUID}"]`)) {
          el.classList.remove("scrolly-active");
        }
        element.classList.add("scrolly-active");
      }
    },
    {
      root: null,
      threshold: 0.5,
      rootMargin: "-40% 0px -40% 0px",
    },
  );

  for (const element of elementsWithFlags) {
    observer.observe(element);
  }

  const cleanup = () => {
    observer.disconnect();
    // Clear all pending timers
    for (const timers of activeTransitions.values()) {
      for (const timer of timers) {
        clearTimeout(timer);
      }
    }
    activeTransitions.clear();
  };
  pool.add(cleanup);

  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) cleanup();
    },
    { once: true },
  );

  rootLogger._debug(`Set up scroll observers for ${elementsWithFlags.length} elements`);
}
